/**
 * Assessment Wizard - Multi-Step Vendor Assessment Flow
 *
 * Guides users through the 4-station vendor assessment:
 * 1. Data Quality - Completeness, entropy, outliers, Benford's Law
 * 2. ML Readiness - Class imbalance, correlation, target suitability
 * 3. Ontology Inference - Column-to-ontology mapping, crosswalk validation
 * 4. FAIR Assessment - 16 SHACL rules validation
 *
 * @epic DC-523 Vendor Certification Program
 * @task DC-531
 */

import React, { useState, useEffect, useCallback } from 'react';
import { BRANDING } from '../../config/branding';
import { useParams, useNavigate } from 'react-router-dom';
import '../../styles/vendor.css';
import { type FileRole, type RoleAction } from '../../components/file-roles';

// ============================================================================
// TYPES
// ============================================================================

type WizardStep = 'setup' | 'upload' | 'data_quality' | 'ml_readiness' | 'ontology' | 'fair' | 'review' | 'submit';

type StationStatus = 'pending' | 'running' | 'passed' | 'failed' | 'skipped';

interface StationResult {
  station: string;
  status: StationStatus;
  score?: number;
  passed?: boolean;
  details?: Record<string, unknown>;
  issues?: StationIssue[];
  startedAt?: string;
  completedAt?: string;
  job_id?: string; // For navigating to analysis results page
}

interface StationIssue {
  severity: 'critical' | 'high' | 'medium' | 'low';
  code: string;
  message: string;
  column?: string;
  recommendation?: string;
}

interface CriticalCheck {
  name: string;
  passed: boolean;
  value?: number;
  threshold?: number;
  message?: string;
}

interface UploadedFile {
  id: string;
  filename: string;
  size: number;
  type: string;
  uploadedAt: string;
  hash?: string;
  // File role - user selects from dropdown (DC-573)
  role?: FileRole;
}

interface CrosswalkFile {
  filename: string;
  mappings: number;
  coverage: number;
  validated: boolean;
}

interface Product {
  product_id: string;
  name: string;
  version?: string;
  category?: string;
}

interface Vendor {
  vendor_id: string;
  name: string;
}

// Phase 8.2: AI/ML Component Types
type AIMLComponentType =
  | 'neural_networks'
  | 'nlp'
  | 'computer_vision'
  | 'reinforcement_learning'
  | 'recommendation_systems'
  | 'anomaly_detection'
  | 'time_series'
  | 'clustering'
  | 'classification'
  | 'regression'
  | 'generative_ai'
  | 'other';

const AIML_COMPONENT_OPTIONS: { value: AIMLComponentType; label: string; description: string }[] = [
  { value: 'neural_networks', label: 'Neural Networks', description: 'Deep learning models, CNNs, RNNs' },
  { value: 'nlp', label: 'Natural Language Processing', description: 'Text analysis, language models, sentiment analysis' },
  { value: 'computer_vision', label: 'Computer Vision', description: 'Image recognition, object detection' },
  { value: 'reinforcement_learning', label: 'Reinforcement Learning', description: 'Decision-making, game AI' },
  { value: 'recommendation_systems', label: 'Recommendation Systems', description: 'Collaborative filtering, content-based' },
  { value: 'anomaly_detection', label: 'Anomaly Detection', description: 'Outlier and irregularity identification' },
  { value: 'time_series', label: 'Time Series Analysis', description: 'Forecasting, trend analysis' },
  { value: 'clustering', label: 'Clustering', description: 'K-means, hierarchical clustering' },
  { value: 'classification', label: 'Classification', description: 'Binary/multi-class classification' },
  { value: 'regression', label: 'Regression', description: 'Linear, polynomial, logistic regression' },
  { value: 'generative_ai', label: 'Generative AI', description: 'LLMs, GANs, diffusion models' },
  { value: 'other', label: 'Other', description: 'Other AI/ML techniques' },
];

interface AssessmentState {
  assessmentId?: string;
  vendorId: string;
  productId: string;
  vendor?: Vendor;
  product?: Product;
  currentStep: WizardStep;
  files: UploadedFile[];
  crosswalk?: CrosswalkFile;
  stations: Record<string, StationResult>;
  criticalChecks: CriticalCheck[];
  overallScore?: number;
  recommendedTier?: 'platinum' | 'gold' | 'silver' | 'bronze' | null;
  notes: string;
  // Phase 8.2: AI/ML Components
  aimlComponents: AIMLComponentType[];
  // Phase 8.3: Vendor invitation email
  vendorInviteEmail?: string;
  showEmailPreview?: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const STEPS: { key: WizardStep; label: string; description: string }[] = [
  { key: 'setup', label: 'Setup', description: 'Configure assessment' },
  { key: 'upload', label: 'Upload', description: 'Upload data files' },
  { key: 'data_quality', label: 'Data Quality', description: 'Analyze data quality' },
  { key: 'ml_readiness', label: 'ML Readiness', description: 'Check ML readiness' },
  { key: 'ontology', label: 'Ontology', description: 'Map to ontologies' },
  { key: 'fair', label: 'FAIR', description: 'Validate FAIR compliance' },
  { key: 'review', label: 'Review', description: 'Review results' },
  { key: 'submit', label: 'Submit', description: 'Submit for certification' },
];

const TIER_THRESHOLDS = {
  platinum: { min: 90, validityMonths: 24 },
  gold: { min: 80, validityMonths: 18 },
  silver: { min: 70, validityMonths: 12 },
  bronze: { min: 60, validityMonths: 6 },
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const StepIndicator: React.FC<{
  steps: typeof STEPS;
  currentStep: WizardStep;
  stationResults: Record<string, StationResult>;
}> = ({ steps, currentStep, stationResults }) => {
  const currentIndex = steps.findIndex((s) => s.key === currentStep);

  return (
    <div className="wizard-steps">
      {steps.map((step, index) => {
        const isCompleted = index < currentIndex;
        const isCurrent = step.key === currentStep;
        const stationResult = stationResults[step.key];
        const hasFailed = stationResult?.status === 'failed';

        return (
          <div
            key={step.key}
            className={`wizard-step ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''} ${hasFailed ? 'failed' : ''}`}
          >
            <div className="step-indicator">
              {isCompleted ? (
                hasFailed ? '✕' : '✓'
              ) : (
                <span>{index + 1}</span>
              )}
            </div>
            <div className="step-info">
              <span className="step-label">{step.label}</span>
              <span className="step-description">{step.description}</span>
            </div>
            {index < steps.length - 1 && <div className="step-connector" />}
          </div>
        );
      })}
    </div>
  );
};

const FileUploadZone: React.FC<{
  files: UploadedFile[];
  onUpload: (files: FileList) => Promise<void>;
  onRemove: (fileId: string) => void;
  onRoleChange?: (fileId: string, role: FileRole) => void;
  uploading: boolean;
}> = ({ files, onUpload, onRemove, onRoleChange, uploading }) => {
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await onUpload(e.dataTransfer.files);
    }
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      await onUpload(e.target.files);
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="file-upload-section">
      <div
        className={`upload-zone ${dragActive ? 'drag-active' : ''} ${uploading ? 'uploading' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="file-upload"
          multiple
          accept=".csv,.tsv,.xlsx,.json,.parquet"
          onChange={handleChange}
          disabled={uploading}
        />
        <label htmlFor="file-upload" className="upload-label">
          {uploading ? (
            <>
              <div className="spinner" />
              <span>Uploading...</span>
            </>
          ) : (
            <>
              <span className="upload-icon">📁</span>
              <span className="upload-text">
                Drag & drop files here or <strong>click to browse</strong>
              </span>
              <span className="upload-hint">
                Supported: CSV, TSV, XLSX, JSON, Parquet (max 100MB)
              </span>
            </>
          )}
        </label>
      </div>

      {files.length > 0 && (
        <div className="uploaded-files">
          <h4>Uploaded Files ({files.length})</h4>
          <ul className="file-list">
            {files.map((file) => (
              <li key={file.id} className="file-item">
                <span className="file-icon">📄</span>
                <div className="file-info">
                  <span className="file-name">{file.filename}</span>
                  <span className="file-meta">{formatSize(file.size)}</span>
                </div>
                {/* File Role Selection - Simple dropdown for user to specify (DC-573) */}
                <div className="file-role" style={{ marginLeft: 'auto', marginRight: '8px' }}>
                  <select
                    value={file.role || 'primary_data'}
                    onChange={(e) => onRoleChange?.(file.id, e.target.value as FileRole)}
                    className="role-select"
                    style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      border: '1px solid #6b7280',
                      fontSize: '13px',
                      backgroundColor: '#fff',
                      color: '#1f2937',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    <option value="primary_data">Primary Data</option>
                    <option value="data_dictionary">Data Dictionary</option>
                    <option value="metadata">Metadata</option>
                    <option value="qc_report">QC Report</option>
                    <option value="crosswalk">Crosswalk</option>
                    <option value="reference">Reference</option>
                    <option value="documentation">Documentation</option>
                  </select>
                </div>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => onRemove(file.id)}
                  title="Remove file"
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const StationPanel: React.FC<{
  station: string;
  label: string;
  description: string;
  result?: StationResult;
  onRun: () => Promise<void>;
  onViewAnalysis?: () => void;
  running: boolean;
}> = ({ station: _station, label, description, result, onRun, onViewAnalysis, running }) => {
  const getStatusIcon = (status?: StationStatus) => {
    switch (status) {
      case 'passed': return '✓';
      case 'failed': return '✕';
      case 'running': return '⟳';
      case 'skipped': return '–';
      default: return '○';
    }
  };

  const getScoreColor = (score?: number) => {
    if (!score) return '';
    if (score >= 80) return 'score-high';
    if (score >= 60) return 'score-medium';
    return 'score-low';
  };

  return (
    <div className={`station-panel ${result?.status || 'pending'}`}>
      <div className="station-header">
        <div className="station-status-icon">{getStatusIcon(result?.status)}</div>
        <div className="station-info">
          <h3>{label}</h3>
          <p>{description}</p>
        </div>
        {result?.score !== undefined && (
          <div className={`station-score ${getScoreColor(result.score)}`}>
            {Math.round(result.score)}%
          </div>
        )}
      </div>

      {result?.issues && result.issues.length > 0 && (
        <div className="station-issues">
          <h4>Issues Found ({result.issues.length})</h4>
          <ul className="issues-list">
            {result.issues.map((issue, idx) => (
              <li key={idx} className={`issue-item severity-${issue.severity}`}>
                <span className="issue-severity">{issue.severity}</span>
                <span className="issue-message">{issue.message}</span>
                {issue.column && (
                  <span className="issue-column">Column: {issue.column}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="station-actions">
        {!result || result.status === 'pending' ? (
          <button
            className="btn btn-primary"
            onClick={onRun}
            disabled={running}
          >
            {running ? 'Running...' : 'Run Analysis'}
          </button>
        ) : (
          <div className="station-complete-actions">
            <span className={`station-complete ${result.status === 'failed' ? 'station-failed' : ''}`}>
              {result.status === 'failed' ? 'Analysis Complete (Failed)' : 'Analysis Complete'}
            </span>
            {onViewAnalysis && result.job_id && (
              <button
                className="btn btn-secondary"
                onClick={onViewAnalysis}
                style={{ marginLeft: '12px' }}
              >
                View Analysis
              </button>
            )}
            <button
              className="btn btn-outline"
              onClick={onRun}
              disabled={running}
              style={{ marginLeft: '8px' }}
            >
              {running ? 'Running...' : 'Re-run'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const CriticalChecksPanel: React.FC<{
  checks: CriticalCheck[];
}> = ({ checks }) => {
  if (checks.length === 0) return null;

  return (
    <div className="critical-checks-panel">
      <h3>Critical Checks</h3>
      <p className="panel-description">
        These checks must pass for certification. Failures in critical checks result in automatic rejection.
      </p>
      <div className="checks-grid">
        {checks.map((check, idx) => (
          <div key={idx} className={`check-card ${check.passed ? 'passed' : 'failed'}`}>
            <div className="check-icon">{check.passed ? '✓' : '✕'}</div>
            <div className="check-info">
              <span className="check-name">{check.name}</span>
              {check.message && <span className="check-message">{check.message}</span>}
              {check.value !== undefined && (
                <span className="check-value">
                  Value: {check.value.toFixed(2)}
                  {check.threshold && ` (threshold: ${check.threshold})`}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// PHASE 8.2: AI/ML COMPONENTS MULTI-SELECT
// ============================================================================

const AIMLComponentsSelect: React.FC<{
  selected: AIMLComponentType[];
  onChange: (selected: AIMLComponentType[]) => void;
}> = ({ selected, onChange }) => {
  const toggleComponent = (value: AIMLComponentType) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div className="aiml-components-select">
      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
        AI/ML Components Used *
      </label>
      <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
        Select all AI/ML components that are part of this product. This helps determine appropriate validation criteria.
      </p>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
        gap: '8px',
      }}>
        {AIML_COMPONENT_OPTIONS.map((option) => {
          const isSelected = selected.includes(option.value);
          return (
            <div
              key={option.value}
              onClick={() => toggleComponent(option.value)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                padding: '10px 12px',
                border: `2px solid ${isSelected ? '#2563eb' : '#e5e7eb'}`,
                borderRadius: '8px',
                backgroundColor: isSelected ? '#eff6ff' : '#fff',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              <div style={{
                width: '20px',
                height: '20px',
                borderRadius: '4px',
                border: `2px solid ${isSelected ? '#2563eb' : '#9ca3af'}`,
                backgroundColor: isSelected ? '#2563eb' : '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '12px',
                flexShrink: 0,
              }}>
                {isSelected && '✓'}
              </div>
              <div>
                <div style={{ fontWeight: 500, fontSize: '14px', color: '#1f2937' }}>{option.label}</div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>{option.description}</div>
              </div>
            </div>
          );
        })}
      </div>
      {selected.length > 0 && (
        <div style={{ marginTop: '12px', fontSize: '13px', color: '#4b5563' }}>
          Selected: {selected.length} component{selected.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// PHASE 8.3: EMAIL PREVIEW MODAL
// ============================================================================

const EmailPreviewModal: React.FC<{
  isOpen: boolean;
  vendorName: string;
  productName: string;
  recipientEmail: string;
  onEmailChange: (email: string) => void;
  onSend: () => Promise<void>;
  onClose: () => void;
}> = ({ isOpen, vendorName, productName, recipientEmail, onEmailChange, onSend, onClose }) => {
  const [sending, setSending] = useState(false);
  const [emailBody, setEmailBody] = useState('');

  // Generate default email body
  useEffect(() => {
    setEmailBody(`Dear ${vendorName} Team,

You have been invited to participate in the AI Data Certification Program for your product "${productName}".

The certification process evaluates your data against industry standards for:
- Data Quality
- ML Readiness
- Ontology Alignment
- FAIR Compliance

Please access the Vendor Portal to upload your data files and complete the assessment.

Best regards,
${BRANDING.productName} Team`);
  }, [vendorName, productName]);

  if (!isOpen) return null;

  const handleSend = async () => {
    setSending(true);
    try {
      await onSend();
      onClose();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <h2>Preview Invitation Email</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="form-group">
            <label htmlFor="recipient-email">Recipient Email</label>
            <input
              id="recipient-email"
              type="email"
              value={recipientEmail}
              onChange={(e) => onEmailChange(e.target.value)}
              placeholder="vendor@example.com"
              style={{ width: '100%' }}
            />
          </div>

          <div className="form-group">
            <label>Email Subject</label>
            <input
              type="text"
              value={`Invitation: AI Data Certification for ${productName}`}
              readOnly
              style={{ width: '100%', backgroundColor: '#f9fafb' }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email-body">Email Body</label>
            <textarea
              id="email-body"
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              rows={12}
              style={{ width: '100%', fontFamily: 'inherit', resize: 'vertical' }}
            />
          </div>

          <div style={{
            padding: '12px',
            backgroundColor: '#fef3c7',
            border: '1px solid #fcd34d',
            borderRadius: '6px',
            fontSize: '13px',
          }}>
            <strong>Note:</strong> The vendor will receive a link to the Vendor Portal where they can upload files and track assessment progress.
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSend}
            disabled={sending || !recipientEmail}
          >
            {sending ? 'Sending...' : 'Send Invitation'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// PHASE 8.4: SAVE DRAFT BUTTON COMPONENT
// ============================================================================

const SaveDraftButton: React.FC<{
  onSave: () => Promise<void>;
  lastSaved?: string;
}> = ({ onSave, lastSaved }) => {
  const [saving, setSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave();
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <button
        className="btn btn-outline"
        onClick={handleSave}
        disabled={saving}
        style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
      >
        {saving ? (
          <>
            <span className="spinner" style={{ width: '14px', height: '14px' }} />
            Saving...
          </>
        ) : showSaved ? (
          <>
            <span style={{ color: '#16a34a' }}>✓</span>
            Saved
          </>
        ) : (
          'Save Draft'
        )}
      </button>
      {lastSaved && (
        <span style={{ fontSize: '12px', color: '#6b7280' }}>
          Last saved: {new Date(lastSaved).toLocaleTimeString()}
        </span>
      )}
    </div>
  );
};

const TierRecommendation: React.FC<{
  score: number;
  tier: 'platinum' | 'gold' | 'silver' | 'bronze' | null;
  criticalChecksPassed: boolean;
}> = ({ score, tier, criticalChecksPassed }) => {
  if (!criticalChecksPassed) {
    return (
      <div className="tier-recommendation tier-none">
        <div className="tier-badge">Not Eligible</div>
        <div className="tier-reason">
          One or more critical checks failed. Address the issues and re-run the assessment.
        </div>
      </div>
    );
  }

  if (!tier) {
    return (
      <div className="tier-recommendation tier-none">
        <div className="tier-badge">Below Threshold</div>
        <div className="tier-reason">
          Score of {Math.round(score)}% is below the minimum threshold of 60% for Bronze certification.
        </div>
      </div>
    );
  }

  const tierInfo = TIER_THRESHOLDS[tier];

  return (
    <div className={`tier-recommendation tier-${tier}`}>
      <div className={`tier-badge cert-badge cert-${tier} cert-lg`}>
        {tier.charAt(0).toUpperCase() + tier.slice(1)}
      </div>
      <div className="tier-details">
        <div className="tier-score">Overall Score: {Math.round(score)}%</div>
        <div className="tier-validity">Valid for {tierInfo.validityMonths} months</div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const AssessmentWizard: React.FC = () => {
  const { vendorId, productId, assessmentId } = useParams<{
    vendorId: string;
    productId: string;
    assessmentId?: string;
  }>();
  const navigate = useNavigate();

  const [state, setState] = useState<AssessmentState>({
    vendorId: vendorId || '',
    productId: productId || '',
    currentStep: 'setup',
    files: [],
    stations: {},
    criticalChecks: [],
    notes: '',
    aimlComponents: [],
    vendorInviteEmail: '',
    showEmailPreview: false,
  });
  const [lastSaved, setLastSaved] = useState<string | undefined>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [runningStation, setRunningStation] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Open user guide in Typora/default markdown viewer
  const handleOpenGuide = async () => {
    try {
      await fetch('/open-guide', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guidePath: 'Assessment_Wizard_Guide.md' })
      });
    } catch (err) {
      console.error('[AssessmentWizard] Error opening guide:', err);
    }
  };

  // Fetch vendor and product info
  const fetchInitialData = useCallback(async () => {
    if (!vendorId || !productId) return;

    setLoading(true);
    try {
      const [vendorRes, productRes] = await Promise.all([
        fetch(`/api/vendors/${vendorId}`),
        fetch(`/api/products/${productId}`),
      ]);

      if (!vendorRes.ok || !productRes.ok) {
        throw new Error('Failed to load vendor or product information');
      }

      const vendorData = await vendorRes.json();
      const productData = await productRes.json();

      // API returns { vendor: {...} } and { product: {...} }
      setState((prev) => ({
        ...prev,
        vendor: vendorData.vendor || vendorData,
        product: productData.product || productData,
      }));

      // If resuming an existing assessment, load its state
      if (assessmentId) {
        // Fetch assessment details and persisted station results in parallel
        const [assessmentRes, stationResultsRes] = await Promise.all([
          fetch(`/api/assessments/${assessmentId}`),
          fetch(`/api/assessments/${assessmentId}/station-results`),
        ]);

        if (assessmentRes.ok) {
          const assessment = await assessmentRes.json();

          // Load persisted station results from database
          let persistedStations: Record<string, StationResult> = {};
          if (stationResultsRes.ok) {
            const { stations: dbStations } = await stationResultsRes.json();
            // Convert database format to client StationResult format
            for (const [stationId, dbResult] of Object.entries(dbStations || {})) {
              const result = dbResult as {
                score: number;
                status: string;
                passed: boolean;
                job_id?: string;
                completed_at?: string;
                results?: { findings?: Array<{ severity: string; message: string; column?: string; recommendation?: string }> };
              };
              persistedStations[stationId] = {
                station: stationId,
                status: result.passed ? 'passed' : 'failed',
                score: result.score,
                passed: result.passed,
                completedAt: result.completed_at,
                job_id: result.job_id,
                issues: (result.results?.findings || []).map((f) => ({
                  severity: f.severity as 'critical' | 'high' | 'medium' | 'low',
                  code: '',
                  message: f.message,
                  column: f.column,
                  recommendation: f.recommendation,
                })),
              };
            }
          }

          setState((prev) => ({
            ...prev,
            assessmentId: assessment.assessment_id,
            files: assessment.evidence_files || [],
            stations: persistedStations,
            criticalChecks: assessment.scores?.critical_checks
              ? Object.entries(assessment.scores.critical_checks as Record<string, { passed: boolean; p_value?: number; coverage?: number }>).map(([name, data]) => ({
                  name,
                  passed: data.passed,
                  value: data.p_value || data.coverage,
                }))
              : [],
            currentStep: determineStep(assessment, persistedStations),
          }));
        }
      } else {
        // No assessmentId in URL - check if there's an existing in-progress assessment for this product
        // This prevents creating duplicate assessments
        const existingRes = await fetch(`/api/assessments?product_id=${productId}&status=requested,in_progress`);
        if (existingRes.ok) {
          const { assessments } = await existingRes.json();
          if (assessments && assessments.length > 0) {
            // Found existing assessment - redirect to resume it
            const existing = assessments[0];
            console.log('[AssessmentWizard] Found existing assessment, redirecting:', existing.assessment_id);
            navigate(`/vendors/${vendorId}/products/${productId}/assess/${existing.assessment_id}`, { replace: true });
            return; // Stop here, the redirect will re-trigger fetchInitialData
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [vendorId, productId, assessmentId, navigate]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const determineStep = (assessment: { status?: string; scores?: { stations?: Record<string, { status?: string; passed?: boolean }> }; evidence_files?: UploadedFile[] }, persistedStations?: Record<string, StationResult>): WizardStep => {
    if (assessment.status === 'submitted' || assessment.status === 'approved') {
      return 'submit';
    }

    // Use persisted stations from database if available, fallback to assessment.scores
    const stations = persistedStations || assessment.scores?.stations || {};

    if (stations.fair?.status === 'passed' || stations.fair?.passed) {
      return 'review';
    }
    if (stations.ontology?.status === 'passed' || stations.ontology?.passed) {
      return 'fair';
    }
    if (stations.ml_readiness?.status === 'passed' || stations.ml_readiness?.passed) {
      return 'ontology';
    }
    if (stations.data_quality?.status === 'passed' || stations.data_quality?.passed) {
      return 'ml_readiness';
    }
    if ((assessment.evidence_files?.length ?? 0) > 0) {
      return 'data_quality';
    }
    return 'upload';
  };

  const handleFileUpload = async (fileList: FileList) => {
    // Use assessmentId from URL params (most reliable after navigation) or state
    const currentAssessmentId = assessmentId || state.assessmentId;

    // Ensure we have an assessmentId before uploading
    if (!currentAssessmentId) {
      setError('Please create an assessment first before uploading files');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      Array.from(fileList).forEach((file) => {
        formData.append('files', file);
      });

      const response = await fetch(`/api/assessments/${currentAssessmentId}/files`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload files');
      }

      const result = await response.json();
      setState((prev) => ({
        ...prev,
        files: [...prev.files, ...result.files],
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleFileRemove = async (fileId: string) => {
    const currentAssessmentId = assessmentId || state.assessmentId;
    try {
      await fetch(`/api/assessments/${currentAssessmentId}/files/${fileId}`, {
        method: 'DELETE',
      });
      setState((prev) => ({
        ...prev,
        files: prev.files.filter((f) => f.id !== fileId),
      }));
    } catch {
      setError('Failed to remove file');
    }
  };

  /**
   * Handle file role change (DC-573)
   * Updates the file role via API and refreshes local state
   */
  const handleFileRoleChange = async (fileId: string, newRole: FileRole) => {
    const currentAssessmentId = assessmentId || state.assessmentId;
    try {
      const response = await fetch(
        `/api/assessments/${currentAssessmentId}/files/${fileId}/role`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ role: newRole }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update file role');
      }

      // Update local state with new role
      setState((prev) => ({
        ...prev,
        files: prev.files.map((f) =>
          f.id === fileId
            ? {
                ...f,
                role: newRole,
                role_confidence: 1.0,
                role_action: 'auto_assign' as RoleAction,
              }
            : f
        ),
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update file role');
    }
  };

  const runStation = async (station: string) => {
    const currentAssessmentId = assessmentId || state.assessmentId;
    console.log(`[AssessmentWizard] Running station: ${station}, assessmentId: ${currentAssessmentId}`);
    setRunningStation(station);
    setState((prev) => ({
      ...prev,
      stations: {
        ...prev.stations,
        [station]: { station, status: 'running', startedAt: new Date().toISOString() },
      },
    }));

    try {
      // Use the correct backend endpoint: /api/assessments/:id/analyze/:station
      const url = `/api/assessments/${currentAssessmentId}/analyze/${station}`;
      console.log(`[AssessmentWizard] Calling: POST ${url}`);
      const response = await fetch(url, {
        method: 'POST',
      });
      console.log(`[AssessmentWizard] Response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[AssessmentWizard] Station ${station} failed:`, errorText);
        throw new Error(`Station ${station} analysis failed: ${errorText}`);
      }

      const result = await response.json();
      console.log(`[AssessmentWizard] Station ${station} result:`, result);
      setState((prev) => ({
        ...prev,
        stations: {
          ...prev.stations,
          [station]: {
            station,
            status: result.passed ? 'passed' : 'failed',
            score: result.score,
            passed: result.passed,
            details: result.details,
            issues: result.issues,
            completedAt: new Date().toISOString(),
            job_id: result.job_id, // Store job_id for "View Analysis" navigation
          },
        },
        criticalChecks: result.criticalChecks
          ? [...prev.criticalChecks.filter((c) => !result.criticalChecks.find((r: CriticalCheck) => r.name === c.name)), ...result.criticalChecks]
          : prev.criticalChecks,
      }));
    } catch (err) {
      setState((prev) => ({
        ...prev,
        stations: {
          ...prev.stations,
          [station]: {
            station,
            status: 'failed',
            issues: [{ severity: 'critical', code: 'ANALYSIS_FAILED', message: err instanceof Error ? err.message : 'Analysis failed' }],
          },
        },
      }));
    } finally {
      setRunningStation(null);
    }
  };

  const createAssessment = async () => {
    try {
      const response = await fetch('/api/assessments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: productId,
          notes: state.notes,
          aiml_components: state.aimlComponents,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create assessment');
      }

      const result = await response.json();
      const newAssessmentId = result.assessment.assessment_id;

      setState((prev) => ({
        ...prev,
        assessmentId: newAssessmentId,
        currentStep: 'upload',
      }));
      setLastSaved(new Date().toISOString());

      // Update URL to include assessmentId so state persists on refresh/navigation
      // This enables resuming the assessment from the correct URL
      navigate(`/vendors/${vendorId}/products/${productId}/assess/${newAssessmentId}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create assessment');
    }
  };

  // Phase 8.4: Save Draft
  const saveDraft = async () => {
    const currentAssessmentId = assessmentId || state.assessmentId;
    if (!currentAssessmentId) return;

    try {
      const response = await fetch(`/api/assessments/${currentAssessmentId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          notes: state.notes,
          aiml_components: state.aimlComponents,
          status: 'draft',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save draft');
      }

      setLastSaved(new Date().toISOString());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save draft');
    }
  };

  // Phase 8.3: Send Vendor Invitation Email
  const sendVendorInvitation = async () => {
    const currentAssessmentId = assessmentId || state.assessmentId;
    if (!currentAssessmentId || !state.vendorInviteEmail) return;

    try {
      const response = await fetch(`/api/assessments/${currentAssessmentId}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: state.vendorInviteEmail,
          vendor_name: state.vendor?.name,
          product_name: state.product?.name,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send invitation');
      }

      setState((prev) => ({ ...prev, showEmailPreview: false }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
    }
  };

  const submitForReview = async () => {
    const currentAssessmentId = assessmentId || state.assessmentId;
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch(`/api/assessments/${currentAssessmentId}/submit`, {
        method: 'POST',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Handle "already submitted" case gracefully
        if (response.status === 400 && errorData.message?.includes('Cannot submit')) {
          // Already submitted - just show success state
          setSubmitted(true);
          return;
        }
        throw new Error(errorData.message || 'Failed to submit assessment');
      }

      // Success!
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const goToStep = (step: WizardStep) => {
    setState((prev) => ({ ...prev, currentStep: step }));
  };

  /**
   * Navigate to the analysis results page (same as journey station "View Results")
   * Opens in new window so user can return to assessment
   */
  const viewAnalysis = (station: string) => {
    const stationResult = state.stations[station];
    const jobId = stationResult?.job_id;

    if (!jobId) {
      console.error(`[AssessmentWizard] No job_id for station ${station}`);
      alert('Analysis results not available. Please re-run the analysis.');
      return;
    }

    // Map station to analysis page URL
    // Legacy pages are served by Express (port 3001), not Vite (port 5173)
    // Hash format is #/tabname?params (parsed by tabRailNav.js)
    const expressBaseUrl = window.location.port === '5173' ? 'http://localhost:3001' : '';
    const routingMap: Record<string, string> = {
      data_quality: `${expressBaseUrl}/index.html#/data-quality?job_id=${jobId}`,
      ml_readiness: `${expressBaseUrl}/index.html#/utils?job_id=${jobId}`,
      ontology: `${expressBaseUrl}/ontology-inference-results-v2.html?job_id=${jobId}&return=assessment`,
      fair: `${expressBaseUrl}/index.html#/live?job_id=${jobId}`,
    };

    const url = routingMap[station];
    if (url) {
      console.log(`[AssessmentWizard] Opening analysis page: ${url}`);
      window.open(url, '_blank');
    } else {
      alert(`Analysis view not configured for ${station}`);
    }
  };

  const nextStep = () => {
    const currentIndex = STEPS.findIndex((s) => s.key === state.currentStep);
    if (currentIndex < STEPS.length - 1) {
      setState((prev) => ({ ...prev, currentStep: STEPS[currentIndex + 1].key }));
    }
  };

  const prevStep = () => {
    const currentIndex = STEPS.findIndex((s) => s.key === state.currentStep);
    if (currentIndex > 0) {
      setState((prev) => ({ ...prev, currentStep: STEPS[currentIndex - 1].key }));
    }
  };

  // Calculate overall score and tier
  const calculateResults = () => {
    const stationScores = Object.values(state.stations)
      .filter((s) => s.score !== undefined)
      .map((s) => s.score!);

    if (stationScores.length === 0) {
      return { score: 0, tier: null, criticalChecksPassed: true };
    }

    const avgScore = stationScores.reduce((a, b) => a + b, 0) / stationScores.length;
    const criticalChecksPassed = state.criticalChecks.every((c) => c.passed);

    let tier: 'platinum' | 'gold' | 'silver' | 'bronze' | null = null;
    if (criticalChecksPassed) {
      if (avgScore >= TIER_THRESHOLDS.platinum.min) tier = 'platinum';
      else if (avgScore >= TIER_THRESHOLDS.gold.min) tier = 'gold';
      else if (avgScore >= TIER_THRESHOLDS.silver.min) tier = 'silver';
      else if (avgScore >= TIER_THRESHOLDS.bronze.min) tier = 'bronze';
    }

    return { score: avgScore, tier, criticalChecksPassed };
  };

  if (loading) {
    return (
      <div className="assessment-wizard">
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading assessment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="assessment-wizard">
        <div className="error-state">
          <p>{error}</p>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const results = calculateResults();

  return (
    <div className="assessment-wizard">
      {/* Header */}
      <div className="wizard-header">
        <button className="btn btn-ghost back-btn" onClick={() => navigate(-1)} title="Exit wizard and return to product page">
          ← Exit Wizard
        </button>
        <div className="wizard-title">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <h1>Product Assessment</h1>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleOpenGuide();
              }}
              className="help-link"
              title="Open Assessment Wizard User Guide"
              style={{
                fontSize: '1.25rem',
                textDecoration: 'none',
                color: '#3b82f6',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                backgroundColor: '#eff6ff',
                border: '1px solid #bfdbfe',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#dbeafe';
                e.currentTarget.style.borderColor = '#3b82f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#eff6ff';
                e.currentTarget.style.borderColor = '#bfdbfe';
              }}
            >
              ?
            </a>
          </div>
          <p>
            {state.vendor?.name} / {state.product?.name}
            {state.product?.version && ` v${state.product.version}`}
          </p>
        </div>
      </div>

      {/* Step Indicator */}
      <StepIndicator
        steps={STEPS}
        currentStep={state.currentStep}
        stationResults={state.stations}
      />

      {/* Step Content */}
      <div className="wizard-content">
        {state.currentStep === 'setup' && (
          <div className="step-content setup-step">
            <h2>Assessment Setup</h2>
            <p>Configure the assessment for {state.product?.name}.</p>

            {/* Phase 8.2: AI/ML Components Selection */}
            <div style={{ marginBottom: '24px' }}>
              <AIMLComponentsSelect
                selected={state.aimlComponents}
                onChange={(components) => setState((prev) => ({ ...prev, aimlComponents: components }))}
              />
            </div>

            <div className="form-group">
              <label htmlFor="notes">Assessment Notes (Optional)</label>
              <textarea
                id="notes"
                value={state.notes}
                onChange={(e) => setState((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="Add any notes or context for this assessment..."
                rows={4}
              />
            </div>

            {/* Phase 8.3: Vendor Invitation Option */}
            <div style={{
              marginTop: '24px',
              padding: '16px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
            }}>
              <h4 style={{ margin: '0 0 8px 0' }}>Invite Vendor to Upload Files</h4>
              <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 12px 0' }}>
                Optionally invite the vendor to upload their data files via the Vendor Portal.
              </p>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="email"
                  placeholder="vendor@example.com"
                  value={state.vendorInviteEmail || ''}
                  onChange={(e) => setState((prev) => ({ ...prev, vendorInviteEmail: e.target.value }))}
                  style={{ flex: 1, padding: '8px 12px' }}
                />
                <button
                  className="btn btn-secondary"
                  onClick={() => setState((prev) => ({ ...prev, showEmailPreview: true }))}
                  disabled={!state.vendorInviteEmail}
                >
                  Preview Email
                </button>
              </div>
            </div>

            <div className="step-actions" style={{ marginTop: '24px' }}>
              <button
                className="btn btn-primary"
                onClick={createAssessment}
                disabled={state.aimlComponents.length === 0}
              >
                Start Assessment
              </button>
              {state.aimlComponents.length === 0 && (
                <span style={{ fontSize: '13px', color: '#dc2626', marginLeft: '12px' }}>
                  Please select at least one AI/ML component
                </span>
              )}
            </div>
          </div>
        )}

        {state.currentStep === 'upload' && (
          <div className="step-content upload-step">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h2 style={{ margin: 0 }}>Upload Data Files</h2>
                <p style={{ margin: '4px 0 0 0' }}>Upload the data files to be assessed. You can upload multiple files.</p>
              </div>
              {/* Phase 8.4: Save Draft Button */}
              <SaveDraftButton onSave={saveDraft} lastSaved={lastSaved} />
            </div>

            <FileUploadZone
              files={state.files}
              onUpload={handleFileUpload}
              onRemove={handleFileRemove}
              onRoleChange={handleFileRoleChange}
              uploading={uploading}
            />

            <div className="step-actions">
              <button className="btn btn-secondary" onClick={prevStep}>
                Back
              </button>
              <button
                className="btn btn-primary"
                onClick={nextStep}
                disabled={state.files.length === 0}
              >
                Continue to Analysis
              </button>
            </div>
          </div>
        )}

        {state.currentStep === 'data_quality' && (
          <div className="step-content station-step">
            <StationPanel
              station="data_quality"
              label="Data Quality Analysis"
              description="Analyze completeness, entropy, outliers, and compare first-digit distributions against the Benford reference (with applicability checks)."
              result={state.stations.data_quality}
              onRun={() => runStation('data_quality')}
              onViewAnalysis={() => viewAnalysis('data_quality')}
              running={runningStation === 'data_quality'}
            />

            <div className="step-actions">
              <button className="btn btn-secondary" onClick={prevStep}>
                Back
              </button>
              <button
                className="btn btn-primary"
                onClick={nextStep}
                disabled={!state.stations.data_quality?.status || state.stations.data_quality?.status === 'pending'}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {state.currentStep === 'ml_readiness' && (
          <div className="step-content station-step">
            <StationPanel
              station="ml_readiness"
              label="ML Readiness Analysis"
              description="Check class imbalance, feature correlation, and target variable suitability for machine learning."
              result={state.stations.ml_readiness}
              onRun={() => runStation('ml_readiness')}
              onViewAnalysis={() => viewAnalysis('ml_readiness')}
              running={runningStation === 'ml_readiness'}
            />

            <div className="step-actions">
              <button className="btn btn-secondary" onClick={prevStep}>
                Back
              </button>
              <button
                className="btn btn-primary"
                onClick={nextStep}
                disabled={!state.stations.ml_readiness?.status || state.stations.ml_readiness?.status === 'pending'}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {state.currentStep === 'ontology' && (
          <div className="step-content station-step">
            <StationPanel
              station="ontology"
              label="Ontology Inference"
              description="Map columns to standard ontologies and validate crosswalk files for proprietary ID mapping."
              result={state.stations.ontology}
              onRun={() => runStation('ontology')}
              onViewAnalysis={() => viewAnalysis('ontology')}
              running={runningStation === 'ontology'}
            />

            <div className="step-actions">
              <button className="btn btn-secondary" onClick={prevStep}>
                Back
              </button>
              <button
                className="btn btn-primary"
                onClick={nextStep}
                disabled={!state.stations.ontology?.status || state.stations.ontology?.status === 'pending'}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {state.currentStep === 'fair' && (
          <div className="step-content station-step">
            <StationPanel
              station="fair"
              label="FAIR Assessment"
              description="Validate against 16 SHACL rules for Findability, Accessibility, Interoperability, and Reusability."
              result={state.stations.fair}
              onRun={() => runStation('fair')}
              onViewAnalysis={() => viewAnalysis('fair')}
              running={runningStation === 'fair'}
            />

            <div className="step-actions">
              <button className="btn btn-secondary" onClick={prevStep}>
                Back
              </button>
              <button
                className="btn btn-primary"
                onClick={nextStep}
                disabled={!state.stations.fair?.status || state.stations.fair?.status === 'pending'}
              >
                Continue to Review
              </button>
            </div>
          </div>
        )}

        {state.currentStep === 'review' && (
          <div className="step-content review-step">
            <h2>Review Results</h2>

            <TierRecommendation
              score={results.score}
              tier={results.tier}
              criticalChecksPassed={results.criticalChecksPassed}
            />

            <CriticalChecksPanel checks={state.criticalChecks} />

            <div className="stations-summary">
              <h3>Station Results</h3>
              <div className="summary-grid">
                {['data_quality', 'ml_readiness', 'ontology', 'fair'].map((station) => {
                  const result = state.stations[station];
                  return (
                    <div
                      key={station}
                      className={`summary-card ${result?.status || 'pending'}`}
                      onClick={() => goToStep(station as WizardStep)}
                    >
                      <span className="summary-label">
                        {station.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                      </span>
                      <span className="summary-score">
                        {result?.score !== undefined ? `${Math.round(result.score)}%` : '-'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="step-actions">
              <button className="btn btn-secondary" onClick={prevStep}>
                Back
              </button>
              <button
                className="btn btn-primary"
                onClick={nextStep}
                disabled={!results.tier}
              >
                Proceed to Submit
              </button>
            </div>
          </div>
        )}

        {state.currentStep === 'submit' && (
          <div className="step-content submit-step">
            {submitted ? (
              // Success confirmation after submission
              <div className="submission-success">
                <div className="success-icon" style={{ fontSize: '4rem', marginBottom: '1rem' }}>✅</div>
                <h2 style={{ color: '#1e293b' }}>Assessment Submitted Successfully!</h2>

                <div className="success-summary" style={{
                  background: '#f8fafc',
                  padding: '1.5rem',
                  borderRadius: '8px',
                  margin: '1.5rem 0',
                  textAlign: 'left',
                  color: '#1e293b'
                }}>
                  <h3 style={{ marginBottom: '1rem', color: '#1e293b' }}>What Happens Next?</h3>
                  <ol style={{ paddingLeft: '1.5rem', lineHeight: '1.8', color: '#334155' }}>
                    <li><strong>Auditor Review:</strong> An auditor will review your submission within 2-5 business days.</li>
                    <li><strong>Additional Information:</strong> You may be contacted if clarification is needed.</li>
                    <li><strong>Certification Decision:</strong> You'll receive notification once a decision is made.</li>
                  </ol>

                  <div style={{ marginTop: '1rem', padding: '1rem', background: '#e2e8f0', borderRadius: '4px', color: '#1e293b' }}>
                    <strong>Predicted Tier:</strong> {results.tier ? results.tier.charAt(0).toUpperCase() + results.tier.slice(1) : 'Pending'}
                    <br />
                    <strong>AI Readiness Score:</strong> {results.score}%
                  </div>
                </div>

                <div className="step-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => navigate(`/vendors/${vendorId}/products/${productId}`)}
                  >
                    Return to Product
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate('/vendors')}
                  >
                    View All Vendors
                  </button>
                </div>
              </div>
            ) : (
              // Pre-submission form
              <>
                <h2>Submit for Certification</h2>

                <div className="submit-summary">
                  <TierRecommendation
                    score={results.score}
                    tier={results.tier}
                    criticalChecksPassed={results.criticalChecksPassed}
                  />

                  <div className="submit-info">
                    <p>
                      By submitting this assessment, you confirm that all data provided is accurate
                      and representative of the product's current state.
                    </p>
                    <p>
                      An auditor will review the submission and may request additional information
                      before final certification decision.
                    </p>
                  </div>
                </div>

                <div className="step-actions">
                  <button className="btn btn-secondary" onClick={prevStep}>
                    Back to Review
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={submitForReview}
                    disabled={submitting || !results.tier}
                  >
                    {submitting ? 'Submitting...' : 'Submit for Certification'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Phase 8.3: Email Preview Modal */}
      <EmailPreviewModal
        isOpen={state.showEmailPreview || false}
        vendorName={state.vendor?.name || 'Vendor'}
        productName={state.product?.name || 'Product'}
        recipientEmail={state.vendorInviteEmail || ''}
        onEmailChange={(email) => setState((prev) => ({ ...prev, vendorInviteEmail: email }))}
        onSend={sendVendorInvitation}
        onClose={() => setState((prev) => ({ ...prev, showEmailPreview: false }))}
      />
    </div>
  );
};

export default AssessmentWizard;
